import { useCallback, useMemo } from 'react';
import {
  InlineButton,
  ControlSizesMD,
} from '@picsart/design-system/components/InlineButton';
import { IconChevronLeft } from '@picsart/design-system/foundation/icons/IconChevronLeft';
import { IconChevronRight } from '@picsart/design-system/foundation/icons/IconChevronRight';
import { IconHelpChat } from '@picsart/design-system/foundation/icons/IconHelpChat';
import { IconFile } from '@picsart/design-system/foundation/icons/IconFile';
import { IconShieldVerified } from '@picsart/design-system/foundation/icons/IconShieldVerified';
import { IconInfoOutline } from '@picsart/design-system/foundation/icons/IconInfoOutline';
import { IconDelete } from '@picsart/design-system/foundation/icons/IconDelete';
import { IconLogout } from '@picsart/design-system/foundation/icons/IconLogout';
import { getContext } from '@/context';
import { logout, deleteAccount } from '@/utils/user/authActions';
import useStyles from './styles';

type IconComponent = typeof IconHelpChat;

interface SettingsLink {
  label: string;
  url: string;
  icon: IconComponent;
}

const formatVersionCode = (raw: string | undefined): string => {
  if (!raw) return '';
  const trimmed = raw.slice(4);
  const major = trimmed.slice(0, 2);
  const minor = trimmed.slice(2, 3);
  const patch = trimmed.slice(3);
  if (!patch || /^0+$/.test(patch)) return `${major}.${minor}`;
  const patchFormatted = patch.length === 1 ? `0${patch}` : patch;
  return `${major}.${minor}.${patchFormatted}`;
};

const HELP_LINKS: SettingsLink[] = [
  {
    label: 'Help Center',
    url: 'https://support.picsart.com/hc/en-us',
    icon: IconHelpChat,
  },
];

const ABOUT_LINKS: SettingsLink[] = [
  {
    label: 'Terms of Use',
    url: 'https://picsart.com/terms-of-use',
    icon: IconFile,
  },
  {
    label: 'License Contribution',
    url: 'https://picsart.com/license/ios/?app=1',
    icon: IconFile,
  },
  {
    label: 'Privacy Policy',
    url: 'https://picsart.com/privacy-policy',
    icon: IconShieldVerified,
  },
  {
    label: 'Do Not Sell My Info',
    url: 'https://privacy.picsart.com/optout?app=1',
    icon: IconShieldVerified,
  },
  {
    label: 'DMCA',
    url: 'https://picsart.com/copyright-dispute-policy',
    icon: IconFile,
  },
];

interface SettingsPanelProps {
  variant: 'dropdown' | 'page';
  onBack?: () => void;
  isAuthorized?: boolean;
}

export const SettingsPanel = ({
  variant,
  onBack,
  isAuthorized = false,
}: SettingsPanelProps) => {
  const classes = useStyles();
  const isPage = variant === 'page';

  const openUrl = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleLogout = useCallback(() => logout(), []);

  const handleDeleteAccount = useCallback(() => deleteAccount(), []);

  const appVersion = useMemo(() => {
    try {
      const versionCode = getContext().utils.api.getHeaders()?.versioncode;
      return formatVersionCode(versionCode);
    } catch {
      return '';
    }
  }, []);

  const renderLinkItem = (link: SettingsLink) => {
    const Icon = link.icon;
    return (
      <button
        key={link.label}
        type="button"
        className={classes.menuItem}
        onClick={() => openUrl(link.url)}
        data-testid={`genai-settings-link-${link.label
          .toLowerCase()
          .replace(/\s+/g, '-')}`}
      >
        <Icon fill="currentColor" className={classes.menuItemIcon} />
        <span className={classes.menuItemLabel}>{link.label}</span>
        <IconChevronRight
          fill="currentColor"
          className={classes.menuItemChevron}
        />
      </button>
    );
  };

  return (
    <div className={isPage ? classes.page : classes.dropdown}>
      {isPage && onBack && (
        <div className={classes.pageHeader}>
          <InlineButton
            dataTestId="genai-settings-back"
            data-testid="genai-settings-back"
            size={ControlSizesMD}
            centerIcon={IconChevronLeft}
            onClick={onBack}
            aria-label="Back"
            isWithoutBorder
            isWithoutBackground
          />
          <span className={classes.pageTitle}>Settings</span>
        </div>
      )}

      {!isPage && (
        <div className={classes.dropdownHeader}>
          <span className={classes.pageTitle}>Settings</span>
        </div>
      )}

      <div className={classes.content}>
        <div className={classes.section}>
          <span className={classes.sectionTitle}>Help</span>
          <div className={classes.sectionItems}>
            {HELP_LINKS.map(renderLinkItem)}
          </div>
        </div>

        <div className={classes.section}>
          <span className={classes.sectionTitle}>About</span>
          <div className={classes.sectionItems}>
            {ABOUT_LINKS.map(renderLinkItem)}
          </div>
        </div>

        {appVersion && (
          <div className={classes.versionRow}>
            <IconInfoOutline
              fill="currentColor"
              className={classes.versionIcon}
            />
            <span className={classes.versionLabel}>Version</span>
            <span className={classes.versionValue}>{appVersion}</span>
          </div>
        )}
      </div>

      {isAuthorized && (
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.dangerItem}
            onClick={handleDeleteAccount}
            data-testid="genai-settings-delete-account"
          >
            <IconDelete fill="currentColor" className={classes.dangerIcon} />
            <span>Delete account</span>
          </button>
          <button
            type="button"
            className={classes.menuItem}
            onClick={handleLogout}
            data-testid="genai-settings-log-out"
          >
            <IconLogout fill="currentColor" className={classes.menuItemIcon} />
            <span className={classes.menuItemLabel}>Log out</span>
          </button>
        </div>
      )}
    </div>
  );
};
